<template>
 <AppCard>
   <v-card-text>
     <v-row>
       <v-col class="d-flex justify-space-between align-center">
          <div class="heading-sm-bold text-text-primary">
            {{ $t('project.question-answer.QuestionAnswerItemShared.questionSharedWith') }}
          </div>
          <AppTooltip>
            <template #activator="{ props }">
              <app-button
                type="icon"
                v-bind="props"
                @click="sharePaneIsOpen = true"
              >
                <app-material-icon name="settings" />
              </app-button>
            </template>
            {{ $t('project.question-answer.QuestionAnswerItemShared.shareActivatorTooltip') }}
          </AppTooltip>
        </v-col>
     </v-row>
     <v-row>
       <v-col>
         <v-skeleton-loader v-if="loading" type="list-item@2" />
         <template v-else>
           <AppTreeView v-for="right in computedRights" :key="right.key" class="mb-3">
             <template #root>
               <span class="ml-2 text-text-secondary">
                 {{ right.computedName }}
               </span>
             </template>

             <template #children>
               <div v-for="member in right.members" :key="member.id">
                 <p class="ml-2 text-text-secondary">
                   {{ member.computedName }}
                 </p>
               </div>
             </template>
           </AppTreeView>
         </template>
       </v-col>
     </v-row>
   </v-card-text>
   <!-- Otherwise we're in a special context where we'd be beside the appbar -->
  <Teleport to="#app">
    <QuestionAnswerItemSharePane v-model="sharePaneIsOpen"
                                 :mnemo="mnemo"
                                 :for-user-id="!isQuestionAuthor ? question?.userId : null"
    />
  </Teleport>
 </AppCard>
</template>

<script>
import { cloneDeep, differenceBy, intersectionBy } from 'lodash-es'
import { mapState, mapActions, mapGetters } from 'vuex'


import AppCard from '@/common/AppCard.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppTreeView from '@/common/AppTreeView.vue'
import QuestionAnswerItemSharePane from '@/project/question-answer/QuestionAnswerItemSharePane.vue'

export default {
  name: 'QuestionAnswerItemShared',
  components: {
    AppTreeView,
    AppCard,
    AppTooltip,
    QuestionAnswerItemSharePane,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      sharePaneIsOpen: false,
    }
  },
  computed: {
    ...mapState('questionAnswer', ['question']),
    ...mapState('groups', ['groups', 'loading']),
    ...mapGetters('user', ['currentUserId']),
    isQuestionAuthor () {
      return this.currentUserId === this.question?.userId
    },
    computedRights () {
      const allGroups = this.groups
      let groupsToShow = cloneDeep(this.groups)
      // If it's shared to everyone, we'll just keep all the groups as the default init
      if (!this.question.rights.isPublic) {
        // We only keep the groups that were shared to, then we'll retrieve the users as we go
        groupsToShow = intersectionBy(groupsToShow, this.question.rights.groups, 'id')
        const groupsNotFullySharedTo = differenceBy(allGroups, groupsToShow, 'id')
        for (const userSharedTo of this.question.rights.users) {
          for (const group of groupsNotFullySharedTo) {
            const userSharedToIsInThatGroup = group.members.some((groupMember) => groupMember.id === userSharedTo.id)
            if (userSharedToIsInThatGroup) {
              const groupInGroupsToShow = groupsToShow.find((groupToShow) => group.id === groupToShow.id)
              // If we already found a member for this group, it's already here
              if (groupInGroupsToShow) {
                // If it's already here, we can push into its members
                groupInGroupsToShow.members.push(userSharedTo)
              } else {
                // We recreate the group and add the first user we found that's in it
                groupsToShow.push({
                  ...group,
                  members: [
                    userSharedTo,
                  ],
                })
              }
              break
            }
          }
        }
      }
      groupsToShow.forEach(group => {
        group.key = `g${group.id}`
        group.computedName = `${group.name} (${group.members.length})`
        group.members.forEach(member => {
          member.key = `m${member.id}`
          member.computedName = `${member.fullName}`
        })
      })
      return groupsToShow
    },
  },
  watch: {
    mnemo: {
      handler () {
        // When navigating to a different room, reload groups
        this.loadGroups()
      },
      immediate: true,
    },
    'question.userId': {
      handler () {
        // When viewing a question from a different author, reload groups
        this.loadGroups()
      },
    },
  },
  mounted () {
    if (this.$route.query.openSharePane) {
      this.sharePaneIsOpen = true
      // Clean up the query param
      // We have to use the direct API because vue-router doesn't seem to be able to remove a param
      history.replaceState(null, '', this.$route.path)
      // We also have to remove it from the route object
      // Otherwise, the next time we open the share pane, it'll be open again
      this.$route.query = {}
    }
  },
  methods: {
    ...mapActions('groups', ['GET_GROUPS', 'GET_GROUPS_FOR_USER']),
    loadGroups () {
      if (this.question?.userId && !this.isQuestionAuthor) {
        this.GET_GROUPS_FOR_USER({ mnemo: this.mnemo, userId: this.question.userId })
      } else {
        this.GET_GROUPS(this.mnemo)
      }
    },
  },
}
</script>
